/**
 * Google Places address autocomplete — shared by both SPAs.
 *
 * Suggests addresses from Places API (New) under OUR text input and, on a pick,
 * reports the formatted address, its parts (street/city/state/ZIP), place_id
 * and lat/lng. We hold no MLS data, so this is address ENTRY only — no listing
 * lookup. Loading and the address split live in ./placesNew.
 *
 * The API key is a PROP, not `import.meta.env` read from in here. This package
 * is consumed as source by two different apps, and a shared component reaching
 * for an env var only one of them happens to define is how you get a control
 * that works in one app and is quietly inert in the other.
 *
 * The input is always ours and always editable. Google is contacted only once
 * three characters are typed (a page that is merely viewed costs nothing and
 * calls nobody), and if Google is unreachable or refuses the key the field
 * stays a plain text box with a one-line note. With NO key it is a plain text
 * box with no note — a bundle built without the key looks exactly like this,
 * which is why both deploy paths refuse to ship one (spec 016, FR-007). Callers
 * that need the split parts must keep their City/State/ZIP fields editable.
 */

import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from "react";
import { createPortal } from "react-dom";
import {
  PLACE_FIELDS,
  loadPlaces,
  onPlacesAuthFailure,
  toSelectedPlace,
  type PlacesLibrary,
  type PlacesPrediction,
  type SelectedPlace,
} from "./placesNew";

export type { SelectedPlace } from "./placesNew";

/** Fewer characters than this match too much to be worth a billed request. */
const MIN_CHARS = 3;
const DEBOUNCE_MS = 250;

type Status = "idle" | "ready" | "unavailable";

export function PlacesAutocomplete({
  apiKey,
  value,
  onChange,
  onSelect,
  className = "rp-input",
  style,
  placeholder = "Property address",
}: {
  /** `import.meta.env.VITE_GOOGLE_MAPS_API_KEY` from the consuming app.
   *  Undefined → the field still works, it just stops suggesting. */
  apiKey: string | undefined;
  value: string;
  onChange: (address: string) => void;
  onSelect: (place: SelectedPlace) => void;
  /** Lets a caller style the field. The inline styles this used to carry
   *  could not be overridden by the page holding it. */
  className?: string;
  /** For callers whose sibling inputs are styled inline rather than by class —
   *  without it this field is visibly the odd one out in the form. */
  style?: CSSProperties;
  /** Shown in every state. It never changes once Google is ready: callers and
   *  tests find the field by it. */
  placeholder?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();
  const noteId = useId();

  const [status, setStatus] = useState<Status>(apiKey ? "idle" : "unavailable");
  const [predictions, setPredictions] = useState<PlacesPrediction[]>([]);
  const [active, setActive] = useState(-1);
  const [box, setBox] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  const libRef = useRef<PlacesLibrary | null>(null);
  const tokenRef = useRef<object | null>(null);
  const requestRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const open = predictions.length > 0;
  const showNote = status === "unavailable" && Boolean(apiKey);

  useEffect(
    () =>
      onPlacesAuthFailure(() => {
        setStatus("unavailable");
        setPredictions([]);
      }),
    [],
  );

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  // The list is portalled to <body>: three callers wrap this input in a
  // <label>, and a click inside a label is forwarded to its input. It is
  // positioned against the DOCUMENT, not the viewport: a fixed list under a
  // field near the bottom of the screen would sit off-screen, and a fixed
  // element cannot be scrolled into view.
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const r = inputRef.current?.getBoundingClientRect();
      setBox(
        r
          ? {
              top: r.bottom + window.scrollY,
              left: r.left + window.scrollX,
              width: r.width,
            }
          : null,
      );
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  const markUnavailable = () => {
    setStatus("unavailable");
    setPredictions([]);
  };

  const suggest = async (input: string) => {
    const request = ++requestRef.current;
    const lib = libRef.current ?? (await loadPlaces(apiKey));
    if (!lib) return markUnavailable();
    libRef.current = lib;
    setStatus("ready");
    tokenRef.current ??= new lib.AutocompleteSessionToken();
    try {
      const { suggestions } =
        await lib.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input,
          sessionToken: tokenRef.current,
        });
      if (request !== requestRef.current) return; // a newer keystroke won
      setPredictions(
        suggestions.flatMap((s) =>
          s.placePrediction ? [s.placePrediction] : [],
        ),
      );
      setActive(-1);
    } catch {
      markUnavailable();
    }
  };

  const handleChange = (text: string) => {
    onChange(text);
    if (timerRef.current) clearTimeout(timerRef.current);
    if (status === "unavailable" || text.trim().length < MIN_CHARS) {
      requestRef.current += 1; // drop anything in flight
      setPredictions([]);
      return;
    }
    timerRef.current = setTimeout(() => void suggest(text), DEBOUNCE_MS);
  };

  const pick = async (prediction: PlacesPrediction) => {
    setPredictions([]);
    requestRef.current += 1;
    const place = prediction.toPlace();
    try {
      await place.fetchFields({ fields: PLACE_FIELDS });
    } catch {
      return markUnavailable();
    }
    const selected = toSelectedPlace(place, prediction.text.text);
    onChange(selected.address);
    onSelect(selected);
    // Picking ends the billing session; the next address starts a new one.
    tokenRef.current = null;
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => (i + 1) % predictions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => (i <= 0 ? predictions.length - 1 : i - 1));
    } else if (e.key === "Enter" && predictions[active]) {
      e.preventDefault(); // pick, don't submit the form
      void pick(predictions[active]);
    } else if (e.key === "Escape") {
      setPredictions([]);
    }
  };

  const optionId = (i: number) => `${listId}-${i}`;

  return (
    <>
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={
          open && active >= 0 ? optionId(active) : undefined
        }
        aria-describedby={showNote ? noteId : undefined}
        value={value}
        placeholder={placeholder}
        onChange={(e) => handleChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => setPredictions([])}
        autoComplete="off"
        className={className}
        style={style}
      />
      {showNote && (
        <span
          id={noteId}
          role="status"
          style={{
            display: "block",
            marginTop: 4,
            fontSize: 12,
            color: "var(--rp-color-text-muted, #6b7280)",
          }}
        >
          Address suggestions are unavailable, so please type the full address.
        </span>
      )}
      {open &&
        box &&
        createPortal(
          <ul
            id={listId}
            role="listbox"
            style={{
              position: "absolute",
              top: box.top + 4,
              left: box.left,
              width: box.width,
              zIndex: 1000,
              margin: 0,
              padding: 4,
              listStyle: "none",
              background: "var(--rp-color-surface, #fff)",
              color: "var(--rp-color-text, #111827)",
              border: "1px solid var(--rp-color-border, #d1d5db)",
              borderRadius: 8,
              boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
              maxHeight: 280,
              overflowY: "auto",
            }}
          >
            {predictions.map((p, i) => (
              <li
                key={p.placeId}
                id={optionId(i)}
                role="option"
                aria-selected={i === active}
                // mousedown, not click: keeps focus in the input so its blur
                // does not close the list before the pick lands.
                onMouseDown={(e) => {
                  e.preventDefault();
                  void pick(p);
                }}
                onMouseEnter={() => setActive(i)}
                style={{
                  padding: "8px 10px",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontSize: 14,
                  background:
                    i === active
                      ? "var(--rp-color-primary-soft, #eef2ff)"
                      : "transparent",
                }}
              >
                {p.text.text}
              </li>
            ))}
          </ul>,
          document.body,
        )}
    </>
  );
}
